'use strict';

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';

import {__} from '~/shared/src/utilities/messageCatalog';
import {PropertyValueType} from '~/shared/src/mm/PropertySet';
import {
    ColumnDefinition,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';

import {Button} from 'primereact/button';
import {
    Dropdown,
    DropdownChangeEvent
} from 'primereact/dropdown';

import {StoreState} from '~/frontend/src/store';
import {createReactMouseEvent} from '~/frontend/src/utilities/reactHelpers';
import {DragElement} from '~/frontend/src/components/base/shared/menu/ColumnSortMenu';


export type Field = {
    key : string;
    field : string;
    label : string;
    valueType : PropertyValueType;
    hidden : boolean;
    hidable : boolean;
};
export type FieldGroup = {
    id : number;
    label : string | JSX.Element;
    fields : Field[]
};
type Fields = ( Field | FieldGroup )[];

type DefaultProps = {
    disabled : boolean;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    disabled : false,
    markDirty: false
};

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    columnDefinitions : ColumnDefinition[];

    defaultSorters : ColumnSorter[];

    /**
     * The value.
     */
    value? : ColumnSorter[];

    /**
     * The value.
     */
    originalValue? : ColumnSorter[];

    tabIndex? : number;

    onChange : ( value : ColumnSorter[] | undefined ) => void;
};

type AllProps = Required<DefaultProps> & Props;


const SortersField = ( props : AllProps ) => {
    const {
              columnDefinitions,
              defaultSorters,
              disabled,
              markDirty,
              onChange,
              tabIndex,
              uid,
              value
          }                                 = props,
          user                              = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp           = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          dragElement                       = useRef<DragElement | undefined>( undefined ),
          boundGlobalMouseMoveHandler       = useRef<( ( e : MouseEvent ) => void ) | undefined>( undefined ),
          boundGlobalMouseUpHandler         = useRef<( ( e : MouseEvent ) => void ) | undefined>( undefined ),
          [ elementValue, setElementValue ] = useState<ColumnSorter[] | undefined>( value ? value : defaultSorters ),
          language                          = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          originalValue                     = useMemo(
              () => {
                  return props.originalValue ?? defaultSorters ?? [];
              },
              [
                  defaultSorters,
                  props.originalValue
              ]
          ),
          fields                            = useMemo(
              () => {
                  const retVal : Fields = [];

                  if ( columnDefinitions?.length ) {
                      for ( const columnDefinition of columnDefinitions ) {
                          retVal.push( {
                              key      : columnDefinition.key,
                              field    : columnDefinition.field,
                              label    : __( columnDefinition.header ),
                              valueType: columnDefinition.valueType ?? 'STRING',
                              hidden   : columnDefinition.hidden ?? false,
                              hidable  : columnDefinition.hidable ?? true
                          } );
                      }
                  }

                  return retVal;
              },
              [
                  columnDefinitions,
                  language,
                  messageCatalogTimestamp
              ]
          ),
          changeElementValue                = useCallback(
              ( newElementValue : ColumnSorter[] ) => {
                  setElementValue( newElementValue );

                  if ( JSON.stringify( newElementValue ) === JSON.stringify( defaultSorters ) || !newElementValue.length ) {
                      onChange( undefined );
                  }
                  else {
                      onChange( newElementValue );
                  }
              },
              [
                  defaultSorters,
                  onChange
              ]
          ),
          onDeleteButtonClick               = useCallback(
              ( field : Field, entry : ColumnSorter ) => {
                  if ( field ) {
                      const newElementValue = ( [] as ColumnSorter[] ).concat( elementValue ?? [] ),
                            index           = newElementValue.findIndex( ( eV ) => eV.field === field.field );

                      if ( index !== -1 ) {
                          newElementValue.splice( index, 1 );
                      }

                      changeElementValue( newElementValue );
                  }
              },
              [
                  changeElementValue,
                  elementValue
              ]
          ),
          onFieldChange                     = useCallback(
              ( entry : ColumnSorter, event : DropdownChangeEvent ) => {
                  const newElementValue = ( [] as ColumnSorter[] ).concat( elementValue ?? [] ),
                        index           = newElementValue.findIndex( ( eV ) => eV.field === entry.field );

                  if ( index !== -1 ) {
                      newElementValue[ index ] = {
                          ...newElementValue[ index ],
                          field: event.value.field
                      };
                  }

                  changeElementValue( newElementValue );
              },
              [
                  changeElementValue,
                  elementValue,
                  fields
              ]
          ),
          onSorterChange                    = useCallback(
              ( entry : ColumnSorter, direction : 'ASC' | 'DESC' ) => {
                  const newElementValue = ( [] as ColumnSorter[] ).concat( elementValue ?? [] ),
                        index           = newElementValue.findIndex( ( eV ) => eV.field === entry.field );

                  if ( index !== -1 ) {
                      newElementValue[ index ] = {
                          ...newElementValue[ index ],
                          direction
                      };
                  }

                  changeElementValue( newElementValue );
              },
              [
                  changeElementValue,
                  elementValue
              ]
          ),
          onAddSorter                       = useCallback(
              ( event : DropdownChangeEvent ) => {
                  const newElementValue = ( [] as ColumnSorter[] ).concat( elementValue ?? [] );

                  newElementValue.push( {
                      key      : event.value.key,
                      field    : event.value.field,
                      direction: 'ASC'
                  } );

                  changeElementValue( newElementValue );
              },
              [
                  changeElementValue,
                  elementValue
              ]
          ),
          onDragMove                        = useCallback(
              ( event : React.MouseEvent ) => {
                  if ( dragElement.current ) {
                      const nativeEvent      = event.nativeEvent,
                            parentElement    = dragElement.current.sorterElement.parentNode as HTMLElement,
                            parentElementBox = parentElement?.getBoundingClientRect(),
                            sorterElements   = parentElement?.querySelectorAll( '.yn-sorter' ),
                            oldY             = parseInt( dragElement.current.style.top ),
                            //x                = Math.round( nativeEvent.pageX - dragElement.current.offsetX ),
                            y                = Math.round( nativeEvent.pageY - dragElement.current.offsetY );

                      // set position
                      //dragElement.current.style.left = x + 'px';
                      dragElement.current.style.top = y + 'px';

                      // position within parent
                      if (
                          /*parentElementBox.x < nativeEvent.pageX && nativeEvent.pageX < parentElementBox.x + parentElementBox.width
                          &&*/ parentElementBox.y < nativeEvent.pageY && nativeEvent.pageY < parentElementBox.y + parentElementBox.height
                      ) {

                          // moved up or down from outside into parent
                          if ( y < oldY ) {

                              // has to change position with a sorter before?
                              for ( let sorterIndex = 0; sorterIndex < dragElement.current.sorterIndex; sorterIndex++ ) {
                                  const sorterElementBox = sorterElements[ sorterIndex ].getBoundingClientRect();

                                  if ( y < sorterElementBox.y + ( sorterElementBox.height / 2 ) ) {
                                      parentElement.removeChild( dragElement.current.sorterElement );

                                      parentElement.insertBefore( dragElement.current.sorterElement, sorterElements[ sorterIndex ] );

                                      dragElement.current.sorterIndex = sorterIndex;

                                      break;
                                  }
                              }
                          }

                          // move down?
                          else if ( y > oldY ) {

                              // has to change position with a sorter after?
                              for ( let sorterIndex = sorterElements.length - 1; sorterIndex > dragElement.current.sorterIndex; sorterIndex-- ) {
                                  const sorterElementBox = sorterElements[ sorterIndex ].getBoundingClientRect();

                                  if ( y > sorterElementBox.y - sorterElementBox.height ) {
                                      parentElement.removeChild( dragElement.current.sorterElement );

                                      if ( sorterIndex < sorterElements.length - 1 ) {
                                          parentElement.insertBefore( dragElement.current.sorterElement, sorterElements[ sorterIndex ].nextSibling );
                                      }
                                      else {
                                          parentElement.appendChild( dragElement.current.sorterElement );
                                      }

                                      dragElement.current.sorterIndex = sorterIndex;

                                      break;
                                  }
                              }
                          }
                      }

                      nativeEvent.preventDefault();
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onDragEnd                         = useCallback(
              ( event : React.MouseEvent ) => {
                  if ( dragElement.current ) {
                      const nativeEvent      = event.nativeEvent,
                            parentElement    = dragElement.current.sorterElement.parentNode as HTMLElement,
                            parentElementBox = parentElement.getBoundingClientRect(),
                            sorterElements   = parentElement.querySelectorAll( '.yn-sorter' );

                      // cleanup global mouse move handler
                      if ( boundGlobalMouseMoveHandler.current ) {
                          document.querySelector( 'html' )?.removeEventListener( 'mousemove', boundGlobalMouseMoveHandler.current );
                          delete boundGlobalMouseMoveHandler.current;
                      }

                      // cleanup global mouseup handler
                      if ( boundGlobalMouseUpHandler.current ) {
                          document.querySelector( 'html' )?.removeEventListener( 'mouseup', boundGlobalMouseUpHandler.current );
                          delete boundGlobalMouseUpHandler.current;
                      }

                      // place at original position if dropped outside parent and not there
                      if (
                          (
                              nativeEvent.pageX < parentElementBox.x
                              || nativeEvent.pageX > parentElementBox.x + parentElementBox.width
                              || nativeEvent.pageY < parentElementBox.y
                              || nativeEvent.pageY > parentElementBox.y + parentElementBox.height
                          )
                          && sorterElements[ dragElement.current.originalSorterIndex ] !== dragElement.current.sorterElement
                      ) {
                          parentElement.removeChild( dragElement.current.sorterElement );

                          if ( dragElement.current.originalSorterIndex < sorterElements.length - 1 ) {
                              parentElement.insertBefore( dragElement.current.sorterElement, sorterElements[ dragElement.current.originalSorterIndex ] );
                          }
                          else {
                              parentElement.appendChild( dragElement.current.sorterElement );
                          }
                      }

                      // reorder sorters
                      if ( dragElement.current.originalSorterIndex !== dragElement.current.sorterIndex ) {
                          const newElementValue = ( [] as ColumnSorter[] ).concat( elementValue ?? [] ),
                                [ sorter ]      = newElementValue.splice( dragElement.current.originalSorterIndex, 1 );

                          newElementValue.splice( dragElement.current.sorterIndex, 0, sorter );

                          changeElementValue( newElementValue );
                      }

                      // set original to visible
                      if ( dragElement.current.sorterElement ) {
                          dragElement.current.sorterElement.classList.remove( 'yn-drag-placeholder' );
                      }

                      // cleanup dragElement
                      document.body.removeChild( dragElement.current );
                      delete dragElement.current;
                  }
              },
              [
                  elementValue
              ]
          ),
          onDragStart                       = useCallback(
              ( event : React.MouseEvent ) => {
                  const nativeEvent      = event.nativeEvent,
                        sorterElement    = ( event.target as HTMLElement ).parentNode as HTMLElement,
                        sorterIndex      = parseInt( sorterElement.id.substr( sorterElement.id.lastIndexOf( '_' ) + 1 ) ),
                        sorterElementBox = sorterElement.getBoundingClientRect();

                  if ( sorterElement ) {
                      // create drag clone
                      dragElement.current = sorterElement.cloneNode( true ) as DragElement;
                      dragElement.current.id = `${uid}_sorter_${sorterIndex}_clone`;
                      dragElement.current.sorterElement = sorterElement;
                      dragElement.current.sorterIndex = sorterIndex;
                      dragElement.current.originalSorterIndex = sorterIndex;
                      dragElement.current.offsetX = nativeEvent.pageX - sorterElementBox.x;
                      dragElement.current.offsetY = nativeEvent.pageY - sorterElementBox.y;

                      dragElement.current.classList.add( 'p-component' );
                      dragElement.current.classList.add( 'yn-drag-item' );

                      dragElement.current.style.left = `calc( ${nativeEvent.pageX - dragElement.current.offsetX}px - .25rem)`;
                      dragElement.current.style.top = ( nativeEvent.pageY - dragElement.current.offsetY ) + 'px';
                      dragElement.current.style.width = sorterElementBox.width + 'px';

                      document.body.appendChild( dragElement.current );

                      // create global mouse move handler
                      boundGlobalMouseMoveHandler.current = ( e : MouseEvent ) => {
                          onDragMove( createReactMouseEvent( e, dragElement.current! ) );
                      };
                      document.querySelector( 'html' )?.addEventListener( 'mousemove', boundGlobalMouseMoveHandler.current );

                      // create global mouseup handler
                      boundGlobalMouseUpHandler.current = ( e : MouseEvent ) => {
                          onDragEnd( createReactMouseEvent( e, dragElement.current! ) );
                      };
                      document.querySelector( 'html' )?.addEventListener( 'mouseup', boundGlobalMouseUpHandler.current );

                      // set original to invisible
                      dragElement.current.sorterElement.classList.add( 'yn-drag-placeholder' );

                      nativeEvent.preventDefault();
                  }
              },
              [
                  onDragEnd,
                  onDragMove
              ]
          ),
          getField                          = ( field : Field, entry : ColumnSorter, fieldTabIndex : number ) => {
              const hasFieldGroups = ( fields?.[ 0 ] as unknown as FieldGroup )?.fields !== undefined;

              let availableFields : Fields = [],
                  fieldMarkDirty           = false,
                  fieldMarkDefault         = false;

              if ( hasFieldGroups ) {
                  const tempAvailableFields : FieldGroup[] = [].concat( fields as [] );

                  for ( const fieldToRemove of ( elementValue ?? [] ) ) {

                      outerFor: for ( let k = tempAvailableFields.length - 1; k >= 0; k-- ) {
                          tempAvailableFields[ k ] = Object.assign( {}, tempAvailableFields[ k ] );
                          tempAvailableFields[ k ].fields = [].concat( tempAvailableFields[ k ].fields as [] );

                          for ( let l = tempAvailableFields[ k ].fields.length - 1; l >= 0; l-- ) {
                              if ( tempAvailableFields[ k ].fields[ l ].field !== field?.field && tempAvailableFields[ k ].fields[ l ].field === fieldToRemove.field ) {
                                  tempAvailableFields[ k ].fields.splice( l, 1 );

                                  if ( !tempAvailableFields[ k ].fields.length ) {
                                      tempAvailableFields.splice( k, 1 );
                                  }

                                  break outerFor;
                              }
                          }
                      }
                  }

                  availableFields = tempAvailableFields;
              }
              else {
                  const tempAvailableFields : Field[] = [].concat( fields as [] );

                  for ( const fieldToRemove of ( elementValue ?? [] ) ) {
                      for ( let k = tempAvailableFields.length - 1; k >= 0; k-- ) {
                          if ( tempAvailableFields[ k ].field !== field?.field && tempAvailableFields[ k ].field === fieldToRemove.field ) {
                              tempAvailableFields.splice( k, 1 );
                              break;
                          }
                      }
                  }

                  availableFields = tempAvailableFields;
              }

              if ( entry ) {
                  if ( markDirty ) {
                      const originalValueEntry = originalValue?.find( oV => oV.field === entry.field );

                      if (
                          !originalValueEntry
                          || originalValueEntry.field !== entry.field
                          || ( originalValue !== defaultSorters && elementValue === defaultSorters )
                      ) {
                          fieldMarkDirty = true;
                      }
                  }

                  if ( elementValue === defaultSorters ) {
                      fieldMarkDefault = true;
                  }
              }

              return <Dropdown
                  value={field}
                  options={availableFields}
                  onChange={onFieldChange.bind( null, entry )}
                  optionLabel="label"
                  optionGroupLabel={hasFieldGroups ? 'label' : undefined}
                  optionGroupChildren={hasFieldGroups ? 'fields' : undefined}
                  className={`yn-jsonfield-sorterfield-field${fieldMarkDirty ? ' yn-dirty-border' : ''}${fieldMarkDefault ? ' status-default' : ''}`}
                  disabled={!field ? false : disabled}
                  tabIndex={fieldTabIndex}
              />;
          },
          getSorter                         = ( field : Field, entry : ColumnSorter, fieldTabIndex : number ) => {
              const sorterLabels = [ '', '' ];

              let fieldMarkDirty   = false,
                  fieldMarkDefault = false;

              switch ( field.valueType ) {
                  case 'BOOLEAN':
                      sorterLabels[ 0 ] = __( 'YN.base.columnSort.sort.asc.boolean' );
                      sorterLabels[ 1 ] = __( 'YN.base.columnSort.sort.desc.boolean' );
                      break;

                  case 'DATE':
                  case 'TIME':
                  case 'TIMESTAMP':
                      sorterLabels[ 0 ] = __( 'YN.base.columnSort.sort.asc.datetime' );
                      sorterLabels[ 1 ] = __( 'YN.base.columnSort.sort.desc.datetime' );
                      break;

                  case 'DOUBLE':
                  case 'INTEGER':
                      sorterLabels[ 0 ] = __( 'YN.base.columnSort.sort.asc.numeric' );
                      sorterLabels[ 1 ] = __( 'YN.base.columnSort.sort.desc.numeric' );
                      break;

                  default:
                      sorterLabels[ 0 ] = __( 'YN.base.columnSort.sort.asc.alpha' );
                      sorterLabels[ 1 ] = __( 'YN.base.columnSort.sort.desc.alpha' );
              }

              if ( entry ) {
                  if ( markDirty ) {
                      const originalValueEntry = originalValue?.find( oV => oV.field === entry.field );

                      if (
                          !originalValueEntry
                          || originalValueEntry.direction !== entry.direction
                          || ( originalValue !== defaultSorters && elementValue === defaultSorters )
                      ) {
                          fieldMarkDirty = true;
                      }
                  }

                  if ( elementValue === defaultSorters ) {
                      fieldMarkDefault = true;
                  }
              }

              return <div className={`yn-jsonfield-sorterfield-sorter p-buttonset ml-2${fieldMarkDirty ? ' yn-dirty-border' : ''}`}>
                  <Button
                      onClick={entry.direction !== 'ASC' ? onSorterChange.bind( null, entry, 'ASC' ) : undefined}
                      label={sorterLabels[ 0 ]}
                      className={`${entry.direction !== 'ASC' ? 'p-button-outlined' : ''}${entry.direction === 'ASC' && fieldMarkDefault ? ' yn-button-default' : ''}`}
                      tabIndex={fieldTabIndex}
                  />
                  <Button
                      onClick={entry.direction !== 'DESC' ? onSorterChange.bind( null, entry, 'DESC' ) : undefined}
                      label={sorterLabels[ 1 ]}
                      className={`${entry.direction !== 'DESC' ? 'p-button-outlined' : ''}${entry.direction === 'DESC' && fieldMarkDefault ? ' yn-button-default' : ''}`}
                      tabIndex={fieldTabIndex + 1}
                  />
              </div>
          },
          getDragHandle                     = ( field : Field, entry : ColumnSorter ) => {
              let fieldMarkDirty   = false,
                  fieldMarkDefault = false;

              if ( entry ) {
                  if ( markDirty ) {
                      const index         = elementValue?.findIndex( oV => oV.field === entry.field ),
                            originalIndex = originalValue?.findIndex( oV => oV.field === entry.field );

                      if (
                          originalIndex === -1
                          || originalIndex !== index
                          || ( originalValue !== defaultSorters && elementValue === defaultSorters )
                      ) {
                          fieldMarkDirty = true;
                      }
                  }

                  if ( elementValue === defaultSorters ) {
                      fieldMarkDefault = true;
                  }
              }

              return <div
                  className={`yn-change-order-button yn-jsonfield-changeorder-button ml-2${elementValue?.length === 1 ? ' p-disabled' : ''}${fieldMarkDirty ? ' yn-dirty' : ''}${fieldMarkDefault ? ' status-default' : ''}`}
                  tabIndex={0}
                  onMouseDown={onDragStart}
                  onMouseUp={onDragEnd}
              ></div>
          },
          getAddSorterField                 = ( fieldTabIndex : number ) => {
              const hasFieldGroups = ( fields?.[ 0 ] as unknown as FieldGroup )?.fields !== undefined,
                    classNames     = [ 'ml-2', 'yn-inputgroup' ];

              let availableFields : Fields = [];

              if ( hasFieldGroups ) {
                  const tempAvailableFields : FieldGroup[] = [].concat( fields as [] );

                  for ( const fieldToRemove of ( elementValue ?? [] ) ) {

                      outerFor: for ( let k = tempAvailableFields.length - 1; k >= 0; k-- ) {
                          tempAvailableFields[ k ] = Object.assign( {}, tempAvailableFields[ k ] );
                          tempAvailableFields[ k ].fields = [].concat( tempAvailableFields[ k ].fields as [] );

                          for ( let l = tempAvailableFields[ k ].fields.length - 1; l >= 0; l-- ) {
                              if ( tempAvailableFields[ k ].fields[ l ].field === fieldToRemove.field ) {
                                  tempAvailableFields[ k ].fields.splice( l, 1 );

                                  if ( !tempAvailableFields[ k ].fields.length ) {
                                      tempAvailableFields.splice( k, 1 );
                                  }

                                  break outerFor;
                              }
                          }
                      }
                  }

                  availableFields = tempAvailableFields;
              }
              else {
                  const tempAvailableFields : Field[] = [].concat( fields as [] );

                  for ( const fieldToRemove of ( elementValue ?? [] ) ) {
                      for ( let k = tempAvailableFields.length - 1; k >= 0; k-- ) {
                          if ( tempAvailableFields[ k ].field === fieldToRemove.field ) {
                              tempAvailableFields.splice( k, 1 );
                              break;
                          }
                      }
                  }

                  availableFields = tempAvailableFields;
              }

              let addDisabled = disabled,
                  placeholder = __( 'YN.base.columnSort.addSorter.placeholder' );

              if ( !availableFields.length ) {
                  addDisabled = true;
                  placeholder = __( 'YN.base.columnSort.addSorter.noMoreColumns' );
              }

              if ( markDirty && !elementValue?.length && JSON.stringify( originalValue ?? '' ) !== JSON.stringify( elementValue ?? '' ) ) {
                  classNames.push( 'yn-dirty-border' );
              }

              return <Dropdown
                  key={`${uid}_sorter_add`}
                  options={availableFields}
                  onChange={onAddSorter}
                  optionLabel="label"
                  optionGroupLabel={hasFieldGroups ? 'label' : undefined}
                  optionGroupChildren={hasFieldGroups ? 'fields' : undefined}
                  className={classNames.join( ' ' )}
                  disabled={addDisabled}
                  placeholder={placeholder}
                  tabIndex={fieldTabIndex}
              />;
          };

    useEffect(
        () => {
            setElementValue( value ? value : defaultSorters );
        },
        [
            defaultSorters,
            value
        ]
    );

    return useMemo(
        () => {
            const hasFieldGroups = ( fields?.[ 0 ] as unknown as FieldGroup )?.fields !== undefined;

            let retVal : React.ReactNode[] = [];

            // elementValue entries rows
            for ( let i = 0; i < ( elementValue?.length ?? 0 ); i++ ) {
                const entry = elementValue![ i ];

                let field;

                if ( hasFieldGroups ) {
                    const fieldGroups : FieldGroup[] = [].concat( fields as [] );

                    outerFor: for ( const fieldGroup of fieldGroups ) {
                        for ( const fieldItem of fieldGroup.fields ) {
                            if ( fieldItem.field === entry.field ) {
                                field = fieldItem;
                                break outerFor;
                            }
                        }
                    }
                }
                else {
                    const tempFields : Field[] = [].concat( fields as [] );

                    for ( const fieldItem of tempFields ) {
                        if ( fieldItem.field === entry.field ) {
                            field = fieldItem;
                            break;
                        }
                    }
                }

                if ( field ) {
                    retVal.push(
                        <div
                            key={`${uid}_sorter_${Math.random()}`}
                            id={`${uid}_sorter_${i}`}
                            className="yn-jsonfield-entry yn-sorter"
                        >
                            <Button
                                disabled={disabled}
                                icon="pi pi-times"
                                className="p-button-danger p-button-text yn-jsonfield-delete-button"
                                onClick={!disabled ? onDeleteButtonClick.bind( null, field, entry ) : undefined}
                                tabIndex={( tabIndex ?? 0 ) + ( 4 * i )}
                            />

                            {getField( field, entry, ( tabIndex ?? 0 ) + ( 4 * i ) + 1 )}
                            {getSorter( field, entry, ( tabIndex ?? 0 ) + ( 4 * i ) + 2 )}
                            {getDragHandle( field, entry )}
                        </div>
                    );
                }
            }

            if ( retVal.length ) {
                retVal = [ <div key={`${uid}_sorter_container`} className="yn-sorter-container">{retVal}</div> ];
            }

            retVal.push( <hr key={`${uid}_sorter_ruler`} className="mt-3 mb-3 yn-ruler"/> );

            // new entry
            retVal.push( getAddSorterField( ( tabIndex ?? 0 ) + ( 4 * ( elementValue?.length ?? 0 ) ) ) );

            return <>{retVal}</>;
        },
        [
            disabled,
            elementValue,
            fields,
            language,
            messageCatalogTimestamp,
            originalValue,
            tabIndex,
            uid
        ]
    );
};
SortersField.defaultProps = defaultProps;
SortersField.displayName = 'JsonField-SortersField';

export default SortersField;
